import { OrthoPalettes } from "@nova/constants"
import { compareFractionalIndices } from "@nova/serializer/src/client/util/fractional_indexing"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import {
    selectAllInfraredFlightsMap,
    selectFlightsWithHotspotOrthos,
} from "features/entity-caching/flights/selectors"
import { selectOrthoMasks } from "features/entity-caching/ortho-masks/selectors"
import { selectActivatedFireId } from "features/map/control/selectors"
import { selectPreviewLayerByIdMapForHotspotOrthos } from "features/map/layers/selectors"
import { useAppSelector } from "integrations/redux/hooks"
import store from "integrations/redux/store"
import { isDefined } from "nodash"
import { CogOrthoLayer } from "plugins/Orthomosaic/layers/CogOrthoLayer"
import { LegacyOrthoLayer } from "plugins/Orthomosaic/layers/LegacyOrthoLayer"
import { selectOrthoComparison } from "plugins/Orthomosaic/OrthoComparisonSlice"
import { selectOrthoOpacityMap } from "plugins/Orthomosaic/OrthomosaicSlice"
import { selectInfraredJobByFlightId } from "../InfraredImageSlice"

export const useIROrthomosaicLayer = () => {
    const workspaceId = useAppSelector(selectActiveWorkspaceId())
    const fireId = useAppSelector(selectActivatedFireId)
    const flights = useAppSelector(selectAllInfraredFlightsMap)
    const orthoLayerById = useAppSelector(selectFlightsWithHotspotOrthos)
    const previewLayerById = useAppSelector(selectPreviewLayerByIdMapForHotspotOrthos)
    const opacityMap = useAppSelector(selectOrthoOpacityMap)
    const comparison = useAppSelector(selectOrthoComparison)
    const orthoMasks = useAppSelector(selectOrthoMasks)
    const orthoMaskEditingFlightId = useAppSelector((state) => state["ortho-mask"]?.editingFlightId)
    // The active comparison flight renders at OrthoComparisonBoost via useOrthoComparisonBoostLayer
    const instances = Object.keys({ ...orthoLayerById, ...previewLayerById })
        .filter((key) => orthoLayerById[key] || previewLayerById[key])
        .filter((key) => key !== comparison.flightId)
        .toSorted((a, b) =>
            compareFractionalIndices(flights[a]?.index ?? "", flights[b]?.index ?? "")
        )
    const state = store.getState()

    return instances
        .map((flightId) => {
            const flight = flights[flightId]
            const job = selectInfraredJobByFlightId(flightId)(state)
            const mask = orthoMasks.find((m) => m.flightId === flightId)
            const shared = {
                id: `IR-Orthomosaic:${flightId}`,
                bbox: flight.orthoBoundingBox,
                maxZoom: job?.maxZoom || 21,
                minZoom: job?.minZoom || 8,
                opacity: opacityMap[flightId] !== undefined ? opacityMap[flightId] / 100 : 1,
                palette: flight.orthoGradient || OrthoPalettes.WhiteHot,
                maskPolygon:
                    mask?.enabled && orthoMaskEditingFlightId !== flightId
                        ? (mask.polygon?.coordinates ?? null)
                        : null,
            }

            const extraFeatures = flight.job?.extraFeatures
            const cogId = flight.cogOrtho?.fileId

            if (!extraFeatures?.includes("cog"))
                return LegacyOrthoLayer({
                    ...shared,
                    fileroot: `/api/organization/${workspaceId}/project/${fireId}/chapter/asd/flight/${flight.demoId || flightId}/orthomosaicTiles`,
                })
            if (extraFeatures.includes("cogId") && !cogId) return null

            return CogOrthoLayer({
                ...shared,
                orgId: workspaceId || "",
                projectId: fireId || "",
                flightId: flight.demoId || flightId || "",
                isDemo: !!flight.demoId,
                cogId,
            })
        })
        .filter(isDefined)
}
